import { useState } from 'react'
import dayjs from 'dayjs'
import { Circle, Loader2, Skull } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useTeam } from 'app/frontend/base/config/context/team'
import { hasAccessToContent } from 'app/frontend/base/config/permissions'
import useDeathTimeline, {
  DeathTimelineSource,
} from '../hooks/useDeathTimeline'
import { MAX_DEATH_LIMIT } from '../utils/deathTimeline'
import PreviousEventMenuItem from './PreviousEventMenuItem'
import EventInsightsEventsDialog from '../../TeamAudit/components/EventInsightsEventsDialog'

const WCL_LOGO_URL = 'https://data.wowaudit.com/img/wcl-logo.png'

type DeathsTimeline = ReturnType<typeof useDeathTimeline>

type Props = {
  deaths: DeathsTimeline
  thumbnailUrl?: string | null
  encounterName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const selectedItemClass =
  'data-[state=checked]:bg-muted data-[state=checked]:text-success-foreground data-[state=checked]:focus:bg-muted data-[state=checked]:focus:text-success-foreground'

const windowOptions: Array<{
  source: DeathTimelineSource
  label: string
  countKey: 'past7Days' | 'past14Days' | 'thisSeason'
}> = [
  { source: '7', label: 'Past 7 days', countKey: 'past7Days' },
  { source: '14', label: 'Past 14 days', countKey: 'past14Days' },
  { source: 'season', label: 'This season', countKey: 'thisSeason' },
]

const DeathsMenuButton = ({
  deaths,
  thumbnailUrl,
  encounterName,
  open,
  onOpenChange,
}: Props) => {
  const { team, guild } = useTeam()
  const canViewLinkedEvents = hasAccessToContent(
    'member',
    'events',
    guild,
    team,
  ).hasAccess
  const [eventsDialogOpen, setEventsDialogOpen] = useState(false)
  const {
    source,
    setSource,
    limit,
    setLimit,
    sources,
    sourcesLoading,
    isLoading,
    active,
  } = deaths
  const [limitDraft, setLimitDraft] = useState(String(limit))

  const changeLimitDraft = (value: string) => {
    setLimitDraft(value)
    const next = Number(value)
    if (value.trim() && Number.isFinite(next)) setLimit(next)
  }

  const selectSource = (next: DeathTimelineSource) => {
    setSource(next === source ? 'hidden' : next)
    onOpenChange(false)
  }

  const changeOpen = (nextOpen: boolean) => {
    if (nextOpen) setLimitDraft(String(limit))
    onOpenChange(nextOpen)
  }

  const linkedEventCount = sources?.linkedEventCount ?? 0
  const scopeEventCount = sources?.scopeEvents.length ?? 0

  return (
    <>
      <DropdownMenu open={open} onOpenChange={changeOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            type='button'
            size='xs'
            variant='outline'
            className={cn(
              'flex-[43] min-w-0 gap-1 px-1',
              active &&
                'text-success-foreground border-success-foreground/60 hover:text-success-foreground',
            )}
            aria-pressed={active}
          >
            {active && isLoading ? (
              <Loader2 className='h-3 w-3 shrink-0 animate-spin' />
            ) : (
              <Skull className='h-3 w-3 shrink-0' />
            )}
            Deaths
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' className='w-52'>
          <DropdownMenuRadioGroup
            value={source}
            onValueChange={(value) =>
              selectSource(value as DeathTimelineSource)
            }
          >
            <DropdownMenuRadioItem value='hidden' className={selectedItemClass}>
              Hide
            </DropdownMenuRadioItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                className={cn(
                  'relative pl-8',
                  source.startsWith('event:') &&
                    'bg-muted text-success-foreground focus:bg-muted focus:text-success-foreground data-[highlighted]:focus:bg-muted data-[highlighted]:focus:text-success-foreground data-[state=open]:bg-muted data-[state=open]:text-success-foreground data-[state=open]:focus:bg-muted',
                )}
              >
                {source.startsWith('event:') ? (
                  <Circle className='absolute left-2 h-2 w-2 fill-current' />
                ) : null}
                Previous event
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className='w-64'>
                  {sourcesLoading ? (
                    <DropdownMenuItem disabled>
                      <span className='flex items-center gap-2'>
                        <Loader2 className='h-4 w-4 animate-spin shrink-0 text-muted-foreground' />
                        Loading events
                      </span>
                    </DropdownMenuItem>
                  ) : (sources?.events.length ?? 0) === 0 ? (
                    <DropdownMenuItem disabled>
                      No logged pulls
                    </DropdownMenuItem>
                  ) : (
                    sources?.events.map((event) => (
                      <PreviousEventMenuItem
                        key={event.id}
                        thumbnailUrl={thumbnailUrl}
                        title={dayjs(event.date).format('ddd, MMM D YYYY')}
                        detail={`${event.pullCount} ${
                          event.pullCount === 1 ? 'pull' : 'pulls'
                        }`}
                        href={`${team.path}/events/${event.id}`}
                        disabled={event.pullCount === 0}
                        selected={source === `event:${event.id}`}
                        onSelect={() => selectSource(`event:${event.id}`)}
                      />
                    ))
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            {windowOptions.map((option) => {
              const count = sources?.[option.countKey]
              return (
                <DropdownMenuRadioItem
                  key={option.source}
                  value={option.source}
                  disabled={count === 0}
                  className={selectedItemClass}
                >
                  <span className='flex w-full items-center justify-between gap-2'>
                    <span>{option.label}</span>
                    {count != null ? (
                      <span className='animate-in fade-in-0 text-xs text-muted-foreground duration-300'>
                        {count} {count === 1 ? 'pull' : 'pulls'}
                      </span>
                    ) : null}
                  </span>
                </DropdownMenuRadioItem>
              )
            })}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <div className='flex items-center gap-2 px-2 py-1.5 text-xs justify-center text-muted-foreground'>
            Count first
            <Input
              type='number'
              min={1}
              max={MAX_DEATH_LIMIT}
              value={limitDraft}
              className='h-6 w-14 px-1.5 text-foreground'
              onChange={(event) => changeLimitDraft(event.currentTarget.value)}
              onBlur={() => setLimitDraft(String(limit))}
            />
            deaths
          </div>
          {canViewLinkedEvents ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setEventsDialogOpen(true)}>
                <span className='flex w-full items-center justify-center gap-2 text-xs'>
                  <img src={WCL_LOGO_URL} alt='' className='h-4 w-4 shrink-0' />
                  {sourcesLoading ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
                  ) : (
                    <span>
                      {linkedEventCount} / {scopeEventCount} events linked
                    </span>
                  )}
                </span>
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {canViewLinkedEvents ? (
        <EventInsightsEventsDialog
          open={eventsDialogOpen}
          onOpenChange={setEventsDialogOpen}
          events={sources?.scopeEvents ?? []}
          isLoading={sourcesLoading}
          title='Events for this encounter'
          description='Past events this season that include this encounter. You can click on each Warcraft Logs badge to view which reports are linked.'
          pullCountByEventId={sources?.pullCounts}
          encounterName={encounterName}
        />
      ) : null}
    </>
  )
}

export default DeathsMenuButton
